import { Box, Chip, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { type ReactNode, useContext, useMemo } from 'react';

import { UserContext } from '~/auth/userContext';
import { HeaderField } from '~/components/common/HeaderField';
import { getTicketDocuments } from '~/components/invoices/detailPage/invoiceDocuments';
import { InvoiceDocumentViewer } from '~/components/invoices/detailPage/InvoiceDocumentViewer';
import { RowPreviewPanel } from '~/components/rowPreview/RowPreviewPanel';
import type { RowPreview } from '~/components/rowPreview/useRowPreview';
import { yesOrNo } from '~/components/utils/booleanToText';
import { useTicketPreview } from '~/data/hooks/useTicket';
import {
  callbackToLabelMap,
  getWorkPerformedLabel,
  resolveTimeLabels,
  type Ticket,
  ticketIsCallback,
} from '~/data/schemas/ticket';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { CARD_SX, pageBandColor, pageBandDividerColor } from '~/theme';
import { canAccess, DOWNLOAD_TICKET_FILE } from '~/utils/role';
import { formatWithTimezone } from '~/utils/timezone';

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <HeaderField label={label}>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {value || '—'}
      </Typography>
    </HeaderField>
  );
}

function FieldGrid({ columns, children }: { columns: number; children: ReactNode }) {
  return (
    <Box
      display="grid"
      gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
      columnGap={3}
      rowGap={1.5}
    >
      {children}
    </Box>
  );
}

/**
 * The ticket's fields for a 600px panel: the two free-text fields get a row each, since
 * they run longest, then when it happened, the hours billed and what was worked on.
 */
function TicketPreviewFields({ ticket }: { ticket: Ticket }) {
  const terms = useLocaleTerms();
  const timeZone = ticket.property?.timeZone;
  const { startedLabel, finishedLabel } = resolveTimeLabels(ticket.entryType);
  const isCallback = ticketIsCallback(ticket);
  const divider = <Divider sx={{ my: 2, borderColor: pageBandDividerColor }} />;

  return (
    <>
      <Stack gap={1.5}>
        <Field
          label="Problem Description"
          value={'problemDescription' in ticket ? ticket.problemDescription : null}
        />
        <Field label={getWorkPerformedLabel(ticket.entryType)} value={ticket.workPerformed} />
      </Stack>
      {divider}
      <FieldGrid columns={3}>
        {isCallback ? (
          <Field
            label="Call Time"
            value={ticket.callTime ? formatWithTimezone(ticket.callTime, timeZone) : null}
          />
        ) : null}
        <Field label={startedLabel} value={formatWithTimezone(ticket.startedTime, timeZone)} />
        <Field label={finishedLabel} value={formatWithTimezone(ticket.finishedTime, timeZone)} />
      </FieldGrid>
      {divider}
      <FieldGrid columns={4}>
        <Field label="Time on site" value={ticket.timeOnSiteRegularHours} />
        <Field label="Travel Time" value={ticket.travelTimeRegularHours} />
        <Field label="Time on site (OT)" value={ticket.timeOnSiteOvertime} />
        <Field label="Travel Time (OT)" value={ticket.travelTimeOvertime} />
      </FieldGrid>
      {divider}
      <FieldGrid columns={3}>
        <Field
          label={terms.vehicles}
          value={ticket.carsServiced.map((car) => car.name).join('\n')}
        />
        <Field label="Problem Type" value={ticket.problemType} />
        <Field label="Equipment Related Failure" value={yesOrNo(ticket.equipmentFailure)} />
      </FieldGrid>
    </>
  );
}

function TicketPreviewContent({ ticketId }: { ticketId: string }) {
  const { ticket, error, loading } = useTicketPreview(ticketId);
  const current = ticket && ticket.id === ticketId ? ticket : undefined;
  const documents = useMemo(() => getTicketDocuments(current), [current]);
  const {
    userDetails: { role },
  } = useContext(UserContext);
  const canSeeFile = canAccess(role, DOWNLOAD_TICKET_FILE);

  if (error && !current) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
        This ticket couldn’t be loaded.
      </Typography>
    );
  }

  return (
    <Box px={2} py={2} sx={{ bgcolor: pageBandColor }}>
      <Stack gap="3px" alignItems="flex-start">
        {current ? (
          <Chip
            size="small"
            variant="outlined"
            label={callbackToLabelMap.get(current.entryType) ?? current.entryType}
          />
        ) : (
          <Skeleton width={120} animation="wave" />
        )}
        <Typography variant="h5" noWrap>
          {current ? (
            `Ticket #${current.number ?? '—'}`
          ) : (
            <Skeleton width={200} sx={{ fontSize: '1.5rem' }} animation="wave" />
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {current ? (
            [
              current.property?.name,
              formatWithTimezone(current.startedTime, current.property?.timeZone),
            ]
              .filter(Boolean)
              .join(' · ')
          ) : (
            <Skeleton width={260} />
          )}
        </Typography>
      </Stack>

      {current ? (
        <>
          <Divider sx={{ my: 2, borderColor: pageBandDividerColor }} />
          <TicketPreviewFields ticket={current} />
        </>
      ) : null}

      {canSeeFile ? (
        <Box mt={2}>
          <InvoiceDocumentViewer
            primaryDocuments={documents}
            supportingDocuments={[]}
            isLoading={loading || !current}
            emptyMessage="No document attached to this ticket."
            sx={{
              ...(documents.length > 0 && { height: '75vh' }),
              ...CARD_SX,
              bgcolor: 'background.paper',
            }}
          />
        </Box>
      ) : null}
    </Box>
  );
}

/** Tickets have no page of their own, so the panel has no Open full page. */
export function TicketPreviewPanel({ rowPreview }: { rowPreview: RowPreview }) {
  return (
    <RowPreviewPanel rowPreview={rowPreview} ariaLabel="Ticket preview">
      {(ticketId) => <TicketPreviewContent ticketId={ticketId} />}
    </RowPreviewPanel>
  );
}
